"use client"

import Image from "next/image"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import "swiper/css"
import type { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { getNonUmbracoUrl, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import { getPaginationWindow } from "../../common/paginationWindow"
import ArrowLeft from "../../icons/ArrowLeft"
import GradientChevron from "../../icons/GradientChevron"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import { breakpoints } from "../../theme/mediaQueries"
import {
  BadgeText,
  CardContent,
  CardDescription,
  CardFooter,
  CardGrid,
  CardHeading,
  CardImageWrapper,
  CardLink,
  CardStatic,
  CardTitle,
  CategoryBadge,
  ChevronWrapper,
  CtaArrow,
  CtaText,
  CtaWrapper,
  EmptyState,
  FilterTab,
  FilterTabs,
  HeaderSection,
  PageButton,
  PageButtonText,
  PaginationArrow,
  PaginationWrapper,
  Root,
  Subtitle,
  SwiperWrapper,
  Title,
  TitleRow,
} from "./styles"

// Desktop = grid + page pagination; mobile can opt into a swipeable carousel
// (EGDE-385). Gated behind `mounted` so SSR/first paint always renders the grid
// (Swiper never runs on the server → no hydration mismatch).
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoints.lg}px)`)
    setIsDesktop(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])
  return isDesktop
}

type LobbyBlockData = components["schemas"]["LobbyBlockPropertiesModel"]
type LobbyItemData = components["schemas"]["LobbyItemPropertiesModel"]
type ApiLinkModel = components["schemas"]["ApiLinkModel"]

interface Props {
  data: LobbyBlockData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

interface ParsedItem {
  id: string
  imageUrl: string | null
  imageAltText: string | null
  categoryTag: string | null
  title: string
  shortDescription: string | null
  ctaLabel: string | null
  ctaHref: string | null
  ctaTarget: string | undefined
  ctaIsInternal: boolean
}

const ALL_KEY = "__all__"
// EGDE-383: the lobby grid shows 3 cards per page by default when the CMS
// "Items Per Page" field is left empty.
const DEFAULT_ITEMS_PER_PAGE = 3

export default function LobbyBlock({ data, culture }: Props) {
  const [activeCategory, setActiveCategory] = useState(ALL_KEY)
  const [currentPage, setCurrentPage] = useState(1)
  const headerRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const isRtl = culture === "he-il" || culture === "he-IL" || culture === "ar"
  const itemsPerPage = data.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE

  // EGDE-385: opt-in mobile carousel (matches the magazine lobby). Off by
  // default → the lobby stays a paginated grid on every viewport.
  const enableCarousel =
    (data as { enableCarousel?: boolean }).enableCarousel === true
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const isDesktop = useIsDesktop()
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const [activeSlide, setActiveSlide] = useState(0)

  const cmsFilterTabs = data.filterTabs ?? []
  const showTabs = data.enableFilterTabs === true && cmsFilterTabs.length > 0

  const allItems: ParsedItem[] = useMemo(() => {
    const blocks =
      (
        data.items as {
          items?: Array<{
            content: { id?: string; properties?: LobbyItemData }
          }>
        }
      )?.items ?? []

    return blocks.map((block, index) => {
      const props = block.content.properties
      const imageRel = getMediaUrl(props?.image as unknown)

      let ctaHref: string | null = null
      let ctaTarget: string | undefined
      let ctaIsInternal = true
      const rawLinks = (props?.ctaLink ?? []) as ApiLinkModel[]
      if (rawLinks.length > 0) {
        const resolved = resolveLinkHref(rawLinks, {
          getNonUmbracoUrl,
          getUmbracoMediaUrl,
        })
        if (resolved) {
          ctaHref = resolved.href
          ctaTarget = resolved.target
          ctaIsInternal = resolved.isInternal
        }
      }

      return {
        id: block.content.id ?? `lobby-item-${index}`,
        imageUrl: imageRel ? getUmbracoMediaUrl(imageRel) : null,
        imageAltText: props?.imageAltText || null,
        categoryTag: props?.categoryTag || null,
        title: props?.title ?? "",
        shortDescription: props?.shortDescription || null,
        ctaLabel: props?.ctaLabel || null,
        ctaHref,
        ctaTarget,
        ctaIsInternal,
      }
    })
  }, [data.items])

  const filteredItems = useMemo(() => {
    if (activeCategory === ALL_KEY) return allItems
    return allItems.filter((item) => item.categoryTag === activeCategory)
  }, [allItems, activeCategory])

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage)

  const showCarousel = mounted && !isDesktop && enableCarousel

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return filteredItems.slice(start, start + itemsPerPage)
  }, [filteredItems, currentPage, itemsPerPage])

  const handleCategoryChange = useCallback(
    (_: unknown, value: string) => {
      setActiveCategory(value)
      setCurrentPage(1)
      setActiveSlide(0)
      swiper?.slideTo(0)
    },
    [swiper],
  )

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage(page)
      // EGDE-384: scroll to the top of the section so the title stays visible
      // just below the compact scroll-pill header (scroll-margin-top clears it).
      // Falls back to the grid when the block has no title/subtitle.
      const target = headerRef.current ?? gridRef.current
      target?.scrollIntoView({ behavior: "smooth", block: "start" })
    },
    [],
  )

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) goToPage(currentPage - 1)
  }, [currentPage, goToPage])

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) goToPage(currentPage + 1)
  }, [currentPage, totalPages, goToPage])

  const allLabel = isRtl ? "הכל" : "All"
  const noResultsLabel = isRtl ? "לא נמצאו תוצאות" : "No results"

  // EGDE-433: at most 5 page circles; the window slides with the current page.
  const pageNumbers = getPaginationWindow(currentPage, totalPages)

  const sectionTitle = data.sectionTitle
  const sectionSubtitle = data.sectionSubtitle

  return (
    <Container paddingMobile>
      <Root>
        {/* EGDE-376: title and description are independent — an empty title must
            not also hide the description. Render the header when either exists. */}
        {(sectionTitle || sectionSubtitle) && (
          <HeaderSection ref={headerRef}>
            {sectionTitle && (
              <TitleRow>
                <ChevronWrapper>
                  <GradientChevron />
                </ChevronWrapper>
                <Title variant="h2">{sectionTitle}</Title>
              </TitleRow>
            )}
            {sectionSubtitle && (
              <Subtitle variant="body1">{sectionSubtitle}</Subtitle>
            )}
          </HeaderSection>
        )}

        {showTabs && (
          <FilterTabs
            value={activeCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons={false}
          >
            <FilterTab label={allLabel} value={ALL_KEY} />
            {cmsFilterTabs.map((tab) => (
              <FilterTab key={tab} label={tab} value={tab} />
            ))}
          </FilterTabs>
        )}

        {filteredItems.length === 0 ? (
          <EmptyState>{noResultsLabel}</EmptyState>
        ) : showCarousel ? (
          <SwiperWrapper ref={gridRef}>
            <Swiper
              dir={isRtl ? "rtl" : "ltr"}
              slidesPerView={1.1}
              spaceBetween={24}
              centeredSlides
              centeredSlidesBounds
              onSwiper={setSwiper}
              onSlideChange={(s) => setActiveSlide(s.activeIndex)}
            >
              {filteredItems.map((item) => (
                <SwiperSlide key={item.id}>
                  <LobbyCard item={item} />
                </SwiperSlide>
              ))}
            </Swiper>
          </SwiperWrapper>
        ) : (
          <CardGrid ref={gridRef}>
            {paginatedItems.map((item) => (
              <LobbyCard key={item.id} item={item} />
            ))}
          </CardGrid>
        )}

        {/* Desktop / grid pagination — numbered pages.
            EGDE-436: match MagazineLobby — DOM order is [prev, pages, next] and
            the RTL flex row mirrors positions natively. Swapping handlers by
            locale on top of that double-reversed the arrows in Hebrew. */}
        {!showCarousel && totalPages > 1 && (
          <PaginationWrapper>
            <PaginationArrow
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronRight />
            </PaginationArrow>
            {pageNumbers.map((n) => (
              <PageButton
                key={n}
                $active={n === currentPage}
                onClick={() => goToPage(n)}
                aria-label={`Page ${n}`}
                aria-current={n === currentPage ? "page" : undefined}
              >
                <PageButtonText $active={n === currentPage}>
                  {n}
                </PageButtonText>
              </PageButton>
            ))}
            <PaginationArrow
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <ChevronLeft />
            </PaginationArrow>
          </PaginationWrapper>
        )}

        {/* Mobile carousel pagination — one dot per slide (EGDE-385) */}
        {showCarousel && filteredItems.length > 1 && (
          <PaginationWrapper>
            <PaginationArrow
              onClick={() => swiper?.slidePrev()}
              disabled={activeSlide <= 0}
              aria-label="Previous slide"
            >
              <ChevronRight />
            </PaginationArrow>
            {/* EGDE-433: cap the dots at a sliding window of 5. */}
            {getPaginationWindow(activeSlide + 1, filteredItems.length).map(
              (page) => (
                <PageButton
                  key={page}
                  $active={page - 1 === activeSlide}
                  onClick={() => swiper?.slideTo(page - 1)}
                  aria-label={`Slide ${page}`}
                  aria-current={page - 1 === activeSlide ? "true" : undefined}
                >
                  <PageButtonText $active={page - 1 === activeSlide}>
                    {page}
                  </PageButtonText>
                </PageButton>
              ),
            )}
            <PaginationArrow
              onClick={() => swiper?.slideNext()}
              disabled={activeSlide >= filteredItems.length - 1}
              aria-label="Next slide"
            >
              <ChevronLeft />
            </PaginationArrow>
          </PaginationWrapper>
        )}
      </Root>
    </Container>
  )
}

function LobbyCard({ item }: { item: ParsedItem }) {
  const inner = (
    <>
      {item.imageUrl && (
        <CardImageWrapper>
          <Image
            src={item.imageUrl}
            alt={item.imageAltText ?? item.title}
            fill
            sizes="(max-width: 767px) 100vw, 33vw"
            style={{ objectFit: "cover" }}
          />
          {item.categoryTag && (
            <CategoryBadge>
              <BadgeText variant="body2">{item.categoryTag}</BadgeText>
            </CategoryBadge>
          )}
        </CardImageWrapper>
      )}
      <CardContent>
        <CardHeading>
          <CardTitle variant="body1">{item.title}</CardTitle>
          {item.shortDescription && (
            <CardDescription variant="body2">
              {item.shortDescription}
            </CardDescription>
          )}
        </CardHeading>
        {item.ctaLabel && (
          <CardFooter>
            <CtaWrapper>
              <CtaArrow>
                <ArrowLeft />
              </CtaArrow>
              <CtaText variant="body1">{item.ctaLabel}</CtaText>
            </CtaWrapper>
          </CardFooter>
        )}
      </CardContent>
    </>
  )

  // EGDE-578: no CMS link → plain box, not a clickable anchor with href="#".
  if (!item.ctaHref) return <CardStatic>{inner}</CardStatic>

  return (
    <CardLink href={item.ctaHref} aria-label={item.title}>
      {inner}
    </CardLink>
  )
}
